import { motion } from "framer-motion";
import { Gauge, TrendingUp, ShieldCheck, Sparkles } from "lucide-react";

const fadeUp = {
  initial: { opacity: 0, y: 30 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-100px" },
  transition: { duration: 0.7, ease: "easeOut" },
};

export default function AIValuation() {
  return (
    <section id="valuation" data-testid="ai-valuation-section" className="relative py-24 md:py-36 overflow-hidden">
      <div className="absolute inset-0 metallic-bg" />
      <div className="absolute inset-0 grid-bg opacity-20" />

      <div className="relative max-w-[1400px] mx-auto px-6 md:px-12">
        <motion.div {...fadeUp} className="max-w-3xl">
          <span className="text-xs font-mono tracking-[0.3em] uppercase text-[#00cccc]">// Smart Valuation Engine</span>
          <h2 className="mt-4 text-4xl sm:text-5xl lg:text-6xl font-black leading-[1.05] tracking-tight">
            India&apos;s Smartest <br />
            <span className="text-gradient-cyan">Motorcycle Valuation</span> Engine.
          </h2>
          <p className="mt-6 text-zinc-400 text-lg max-w-2xl leading-relaxed">
            Our smart engine analyzes thousands of real-time market signals to determine the ideal buying price for your premium motorcycle.
          </p>
        </motion.div>

        {/* Dashboard */}
        <motion.div {...fadeUp} transition={{ duration: 0.8, delay: 0.2 }} className="mt-14 relative">
          <div className="grid grid-cols-1 gap-5">
            {/* Main price panel */}
            <div className="relative p-7 rounded-2xl glass overflow-hidden">
              <div className="absolute top-0 left-0 right-0 h-[1px] bg-gradient-to-r from-transparent via-[#00cccc] to-transparent" />
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-2">
                  <span className="w-2 h-2 rounded-full bg-[#00cccc] animate-pulse-glow" />
                  <span className="text-[10px] font-mono tracking-[0.25em] uppercase text-[#00cccc]">Live Analysis</span>
                </div>
                <span className="text-[10px] font-mono text-zinc-500">BB-SMART v4.2</span>
              </div>
              <div className="text-xs font-mono text-zinc-500 mb-1">KTM DUKE 390 / 2023 / 4,200 KM</div>
              <div className="flex items-baseline gap-3">
                <span className="text-5xl md:text-6xl font-mono font-bold text-white">₹2,84,500</span>
                <span className="text-[#00cccc] text-sm font-mono">+₹12,400 above avg</span>
              </div>

              <div className="mt-8 grid grid-cols-2 gap-5">
                {[
                  { icon: ShieldCheck, label: "Confidence", val: 98, suffix: "%" },
                  { icon: TrendingUp, label: "Demand Score", val: 92, suffix: "/100" },
                  { icon: Gauge, label: "Health Score", val: 96, suffix: "/100" },
                  { icon: Sparkles, label: "Ownership", val: 89, suffix: "/100" },
                ].map((m) => (
                  <div key={m.label} className="p-4 rounded-xl bg-white/[0.04] border border-white/10">
                    <div className="flex items-center gap-2 mb-3">
                      <m.icon className="w-3.5 h-3.5 text-[#00cccc]" />
                      <span className="text-xs font-sans font-semibold uppercase tracking-wider text-zinc-300">{m.label}</span>
                    </div>
                    <div className="flex items-baseline gap-1">
                      <span className="text-2xl font-mono font-bold text-white">{m.val}</span>
                      <span className="text-xs text-zinc-500 font-mono">{m.suffix}</span>
                    </div>
                    <div className="mt-3 h-1 bg-white/[0.06] rounded-full overflow-hidden">
                      <motion.div
                        initial={{ width: 0 }}
                        whileInView={{ width: `${m.val}%` }}
                        viewport={{ once: true }}
                        transition={{ duration: 1.4, ease: "easeOut" }}
                        className="h-full bg-[#00cccc]"
                        style={{ boxShadow: "0 0 8px #00cccc" }}
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>

          </div>

          {/* Insight cards row */}
          <div className="mt-5 grid grid-cols-1 md:grid-cols-3 gap-5">
            {[
              { icon: Gauge, label: "Mileage Impact", val: "+₹4,200" },
              { icon: TrendingUp, label: "Resale Trend", val: "Strong" },
            ].map((c, i) => (
              <motion.div
                key={c.label}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.08 }}
                className="p-5 rounded-xl glass hover:border-[#00cccc]/40 transition-all"
                data-testid={`insight-card-${c.label.toLowerCase().replace(/\s+/g, "-")}`}
              >
                <c.icon className="w-4 h-4 text-[#00cccc] mb-3" />
                <div className="text-xs font-sans font-semibold uppercase tracking-wider text-zinc-300">{c.label}</div>
                <div className="text-lg font-mono text-white mt-1">{c.val}</div>
              </motion.div>
            ))}
            {/* Smart Confidence */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.16 }}
              className="p-5 rounded-xl glass hover:border-[#00cccc]/40 transition-all"
              data-testid="insight-card-smart-confidence"
            >
              <div className="flex items-center justify-between mb-3">
                <span className="text-xs font-sans font-semibold uppercase tracking-wider text-zinc-300">Smart Confidence</span>
                <ShieldCheck className="w-4 h-4 text-[#00cccc]" />
              </div>
              <div className="flex items-baseline gap-2">
                <span className="text-2xl font-mono font-bold text-white">98.4%</span>
                <span className="text-[10px] font-mono text-zinc-500">match score</span>
              </div>
              <div className="mt-3 h-1.5 bg-white/[0.06] rounded-full overflow-hidden">
                <motion.div
                  initial={{ width: 0 }}
                  whileInView={{ width: "98%" }}
                  viewport={{ once: true }}
                  transition={{ duration: 1.4, ease: "easeOut" }}
                  className="h-full bg-[#00cccc]"
                  style={{ boxShadow: "0 0 10px #00cccc" }}
                />
              </div>
              <p className="mt-3 text-[11px] text-zinc-500 leading-relaxed">
                Cross-checked against verified resale data, condition signals, and live demand.
              </p>
            </motion.div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
